import { Component, OnInit, ViewChild, ElementRef, HostListener } from '@angular/core';
import { BaseComponent } from '../classes/BaseComponent';
import { ReferralService } from '../referral.service';
import { StorageHelper } from '../helpers/StorageHelper';
import { ReferralData, Product, Transaction } from '../classes/IReferralInterfaces';
import { Global } from '../classes/Global';
import { ShopService } from '../shop.service';
import { StringHelper } from '../helpers/StringHelper';
import { PriceHelper } from '../helpers/PriceHelper';
import Swal, { SweetAlertResult } from 'sweetalert2';
import { DateHelper } from '../helpers/DateHelper';

@Component({
  selector: 'app-redeem',
  templateUrl: './redeem.component.html',
  styleUrls: ['./redeem.component.scss'],
  providers: [ ReferralService, ShopService ]
})
export class RedeemComponent extends BaseComponent implements OnInit {

  referralData: ReferralData;
  products: Product[] = [];
  transactions: Transaction[] = [];
  noProductsAvailable: boolean = false;
  resendCodeDebounce: any = {};

  countries: any[] = [
    Global.countries.us,
    Global.countries.ca,
    Global.countries.gb,
    Global.countries.de,
    Global.countries.au
  ];
  @ViewChild('countryDDContent', {static: false}) countryDDContent: ElementRef;
  selectedCountry: any = this.countries.filter(c => c.key == Global.getCountry())[0] ||  this.countries[0];

  @HostListener('document:click', ['$event'])
  clickout(event) {
    if (!event || !event.target) {
      return;
    }
    try {
      // Safely check className - handle cross-origin iframe elements
      let targetClassName = '';
      let parentClassName = '';
      try {
        const className = event.target.className;
        if (className) {
          if (typeof className === 'string') {
            targetClassName = className;
          } else if (className && typeof className === 'object') {
            if (className.baseVal !== undefined) {
              targetClassName = className.baseVal || '';
            } else if (typeof className.toString === 'function') {
              targetClassName = className.toString();
            }
          }
        }
        if (event.target.parentElement) {
          const parentClassNameObj = event.target.parentElement.className;
          if (parentClassNameObj) {
            if (typeof parentClassNameObj === 'string') {
              parentClassName = parentClassNameObj;
            } else if (parentClassNameObj && typeof parentClassNameObj === 'object') {
              if (parentClassNameObj.baseVal !== undefined) {
                parentClassName = parentClassNameObj.baseVal || '';
              } else if (typeof parentClassNameObj.toString === 'function') {
                parentClassName = parentClassNameObj.toString();
              }
            }
          }
        }
      } catch (e) {
        // Permission denied when accessing className from cross-origin iframe - ignore
        return;
      }
      if (targetClassName != "dd-selected" && parentClassName != "dd-selected" && this.countryDDContent) {
        this.closeDropdown(this.countryDDContent);
      }
    } catch (e) {
    // Ignore permission errors from cross-origin iframes or restricted contexts
    }
  }

  constructor(
    private referralService: ReferralService,
    private shopService: ShopService
  ) {
    super();
  }

  ngOnInit() {
    this.getReferralData();
    this.getProducts();
    this.getTransactions();
  }

  closeDropdown(dropdownRef: ElementRef) {
    if (dropdownRef && dropdownRef.nativeElement) {
      dropdownRef.nativeElement.className = 'dropdown-content dd-close';
    }
  }

  openDropdown(dropdownRef: ElementRef) {
    if (dropdownRef && dropdownRef.nativeElement) {
      dropdownRef.nativeElement.className = 'dropdown-content dd-open';
    }
  }

  selectCountry(country: any) {
    this.selectedCountry = country;
    this.getProducts();
  }

  countryClicked() {
    if (this.countryDDContent && this.countryDDContent.nativeElement && this.countryDDContent.nativeElement.classList.contains('dd-open')) {
      this.closeDropdown(this.countryDDContent);
    }
    else {
      this.openDropdown(this.countryDDContent);
    }
  }

  getReferralData() {
    this.referralService.getUserReferralData(
      StorageHelper.getInstance().get(StorageHelper.keys.sessionToken),
      Global.getCountry()
    ).subscribe((res: any) => {
      if(res && res.user) {
        this.referralData = res as ReferralData;
      }
    }, err => {
      console.error(err);
    });
  }

  getProducts() {
    this.shopService.getProducts(
      StorageHelper.getInstance().get(StorageHelper.keys.sessionToken),
      Global.getCountry(),
      this.selectedCountry.key
    ).subscribe(res => {
      if(res && res['results'] && res['results'].length > 0) {
        this.products = res['results'] as Product[];
        this.noProductsAvailable = false;
      }
      else {
        this.products = [];
        this.noProductsAvailable = true;
      }
    }, err => {
      console.error(err);
    });
  }

  getTransactions() {
    this.shopService.getUserTransactions(
      StorageHelper.getInstance().get(StorageHelper.keys.sessionToken),
      Global.getCountry()
    ).subscribe(res => {
      if(res && res['results'] && res['results'].length > 0) {
        this.transactions = res['results'] as Transaction[];
      }
    }, err => {
      console.error(err);
    });
  }

  order(product: Product) {

    Swal.fire({
      title: this.getString("areYouSure"),
      text: this.getString("youWontBeAbleToRevertThis"),
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#00BAF7',
      cancelButtonColor: '#d33',
      cancelButtonText: this.getString("cancel"),
      confirmButtonText: this.getString("yes"),
      reverseButtons: true
    }).then(result => {
      if (result.value) {
        this.shopService.redeemProduct(
          product.id.toString(),
          StorageHelper.getInstance().get(StorageHelper.keys.sessionToken),
          Global.getCountry()).subscribe(res => {
            if(res && res['status'] == "success") {
              this.showSuccessPopup(this.getString('redeemSuccessMessage')).then(() => {
                this.getTransactions();
                this.getReferralData();
              });
            }
            else if(res && res['status'] == "error") {
              if(res['message'] == "User have not enough points to redeem this product.") {
                this.showWarningPopup(this.getString('notEnoughPoints'));
              }
            }
          }, err => {
            console.error(err);
            this.showErrorPopup(this.getString('serverError'));
          });
      }
    });
  }

  getCardPrice(product: Product): string {
    return PriceHelper.formatPriceWithoutCurrency(product.productValue, product.productCurrency);
  }

  getCardCurrency(product: Product): string {
    return PriceHelper.getCurrencySymbol(product.productCurrency);
  }

  getFlagSrc(product: Product): string {
    const country = Global.countries[product.productCountry];
    if(country) {
      return country.icon;
    }
    return "";
  }

  getCardSubText(product: Product): string {
    return StringHelper.format(this.getString('itunesCardSubText'), PriceHelper.formatPrice(product.productValue, product.productCurrency, true, true, false), product.productCountry.toUpperCase());
  }

  showSuccessPopup(message: string): Promise<SweetAlertResult> {
    return Swal.fire({
      title: '',
      icon: 'success',
      text: message,
      showCloseButton: false,
      showCancelButton: false,
      focusConfirm: true,
      confirmButtonText: this.getString("ok"),
      confirmButtonAriaLabel: this.getString("ok")
    });
  }

  showWarningPopup(message: string) {
    Swal.fire({
      title: '',
      icon: 'warning',
      text: message,
      showCloseButton: false,
      showCancelButton: false,
      focusConfirm: true,
      confirmButtonText: this.getString("ok"),
      confirmButtonColor: "#00BAF7",
      confirmButtonAriaLabel: this.getString("ok")
    });
  }

  showErrorPopup(message: string) {
    Swal.fire({
      title: '',
      icon: 'error',
      text: message,
      showCloseButton: false,
      showCancelButton: false,
      focusConfirm: true,
      confirmButtonText: this.getString("ok"),
      confirmButtonColor: "#00BAF7",
      confirmButtonAriaLabel: this.getString("ok")
    });
  }

  resendCode(transaction: Transaction) {

    if(!this.resendCodeDebounce[transaction.id]) {
      this.resendCodeDebounce[transaction.id] = true;
      setTimeout(() => {
        this.resendCodeDebounce[transaction.id] = false;
      }, 1000);

      this.shopService.resendCode(
        StorageHelper.getInstance().get(StorageHelper.keys.sessionToken),
        Global.getCountry(),
        transaction.id.toString()
      ).subscribe(res => {
        if(res && res['status'] == "success") {
          this.showSuccessPopup(this.getString('codeSentSuccessMessage'));
        }
        else if(res && res['status'] == "error") {
          this.showErrorPopup(this.getString('serverError'));
        }
      }, err => {
        console.error(err);
        this.showErrorPopup(this.getString('serverError'));
      });

    }

  }

  getFormattedDate(dateObj: any): string {
    return DateHelper.serverDateObjToDateString(dateObj);
  }

  getDisplayProductType(product: Product) {
    if("ITUNES_GIFT_CARD" == product.productType) {
      return StringHelper.format(this.getString("productTypeiTunesGiftCard"),
      PriceHelper.formatPrice(product.productValue, product.productCurrency, true, true, false),
      product.productCountry.toUpperCase());
    }
    return product.productType;
  }


}
